/** @format */
/** @format */

import React, { FC, useState, useMemo } from 'react'
import styled from 'styled-components'
import { _$, Drawer, Row, Col } from '@king-design/react'
import { useAsync } from 'react-use'
import { CloseFooter, TablePage } from 'src/components'
import { alarmColor, alarmTitle } from './constants'
import Api from 'src/api/ApiCloud'
import { request } from 'src/utils'

interface NodeDrawerProps {
  className?: string
  visible: boolean
  onClose: () => void
  dataSource: string
  nodeData: any
}

const NodeDrawer: FC<NodeDrawerProps> = ({ className, visible, onClose, dataSource, nodeData }) => {
  const [tableGroup, setTableGroup] = useState<{ Severity: string }>({ Severity: '' })
  const [page, setPage] = useState<{ limit: number; value: number }>({
    value: 1,
    limit: 10
  })

  const { value: port_value, loading: port_loading } = useAsync(async () => {
    if (visible && nodeData?.GUID) {
      const result = await request(Api.getUFMPort, {
        data: { DataSource: dataSource, System: nodeData?.GUID }
      })
      return result?.data?.ListPortsResult?.Data || []
    } else {
      return []
    }
  }, [nodeData, visible])

  const tableData = useMemo(() => {
    if (port_value?.length) {
      const { Severity } = tableGroup
      return Severity ? port_value.filter(item => item.Severity === Severity) : port_value
    } else {
      return []
    }
  }, [port_value, tableGroup])

  const scheme: any = {
    NodeDescription: {
      title: _$('名称')
    },
    Severity: {
      title: _$('状态'),
      width: 200,
      hideGroupHeader: true,
      group: [
        { label: _$('全部'), value: '' },
        ...Object.keys(alarmTitle).map(key => ({ label: alarmTitle[key], value: key }))
      ],
      template: ({ Severity }) => (
        <span className={`severity ${Severity?.toLowerCase()}`}>
          {alarmTitle[Severity] || Severity}
        </span>
      )
    }
  }
  return (
    <div id="node_drawer">
      <Drawer
        value={visible}
        width={600}
        container={() => document.getElementById('node_drawer')}
        slotFooter={<CloseFooter onClose={onClose} />}
        onChangeValue={show => {
          if (!show) {
            setTableGroup({ Severity: '' })
            setPage({
              value: 1,
              limit: 10
            })
            onClose()
          }
        }}
        title={_$('设备详情')}
      >
        <div className={className}>
          <p className="title" style={{ marginTop: 0 }}>
            {_$('设备信息')}
          </p>
          <Row gutter="16" className="cable_info">
            <Col span="12">
              <p>
                {_$('名称')}
                <span>{nodeData?.param?.SystemName || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                {_$('IP')}
                <span>{nodeData?.param?.IP || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                {_$('GUID')}
                <span>{nodeData?.param?.GUID || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                {_$('设备类型')}
                <span>{nodeData?.param?.Type || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                {_$('所属模型')}
                <span>{nodeData?.param?.Model || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                {_$('严重等级')}
                <span className={`severity ${nodeData?.param?.Severity?.toLowerCase()}`}>
                  {alarmTitle[nodeData?.param?.Severity] || ''}
                </span>
              </p>
            </Col>
            <Col span="12">
              <p>
                {_$('FW版本')}
                <span>{nodeData?.param?.FWVersion || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                PSID<span>{nodeData?.param?.PSID || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                {_$('总告警数')}
                <span>{nodeData?.param?.TotalAlarms || 0}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                {_$('温度')}
                <span>{nodeData?.param?.Temperature || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                {_$('描述')}
                <span>{nodeData?.param?.Description || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                {_$('SW版本')}
                <span>{nodeData?.param?.SWVersion || ''}</span>
              </p>
            </Col>
          </Row>
          <p className="title">{_$('端口信息')}</p>
          <TablePage
            tableProps={{
              loading: port_loading,
              data: tableData,
              checkType: 'none',
              scheme: scheme,
              group: tableGroup,
              onChangeGroup: group => {
                setTableGroup(group)
                setPage({ value: 1, limit: page.limit })
              },
              pagination: {
                total: tableData?.length,
                ...page
              },
              onPage: ({ value, limit }) => {
                setPage({ value, limit })
              }
            }}
          />
        </div>
      </Drawer>
    </div>
  )
}

export default styled(NodeDrawer)`
  .title {
    margin-top: 16px;
    padding: 12px 20px 12px 20px;
    height: 52px;
    line-height: 28px;
    background: #f8f8fa;
    color: #3c4449;
    font-size: 14px;
  }
  .cable_info {
    p {
      padding: 8px 0;
      margin: 0;
      color: #b0b8d7;
      span {
        margin-left: 8px;
        color: #3c4449;
      }
    }
  }

  .severity {
    &::before {
      content: '';
      border-radius: 50%;
      display: inline-block;
      height: 8px;
      width: 8px;
      margin-right: 6px;
      position: relative;
      top: -1px;
    }
    &.info::before {
      background: ${alarmColor.Info};
    }
    &.warning::before {
      background: ${alarmColor.Warning};
    }
    &.minor::before {
      background: ${alarmColor.Minor};
    }
    &.critical::before {
      background: ${alarmColor.Critical};
    }
  }
`
